import { useMemo, useState } from 'react';
import {
  Banner,
  Button,
  Form,
  FormLayout,
  Modal,
  ModalBody,
  ModalFooter,
  TextArea,
  TextField,
  useModal,
  useSnackbars,
} from '@customerio/pluma-components/react';
import type { PlumaManagedModalProps } from '@customerio/pluma-components/react';
import { z } from '@customerio/hydra-validation';
import { useZodForm } from '@/components/form';
import { useCreateCollection, useUpdateCollection } from '@/services/db/collection/mutations';
import type { Collection } from '@/services/db/collection/entity';
import { buildCreateCollectionBody, buildUpdateCollectionRequest, type ImportSource } from './collection-payload';
import { ImportSourceField } from './import-source-field';

const CREATE_FORM_ID = 'create-collection-form';
const IMPORT_REQUIRED_ERROR = 'A file must be imported.';

export type CollectionFormModalData = {
  /** Present ⇒ update (re-import) mode; absent ⇒ create mode. */
  collection?: Collection;
  /** Threaded in because managed modals render above the router/AuthProvider. */
  workspaceId: number;
  existingNames?: string[];
  maxFileSizeBytes: number | undefined;
  onSaved?: () => void;
};

export function CollectionFormModal({ modalState, data, onClose }: PlumaManagedModalProps<CollectionFormModalData>) {
  const { collection, workspaceId, existingNames = [], maxFileSizeBytes, onSaved } = data;

  const handleDone = () => {
    onSaved?.();
    onClose();
  };

  return (
    <Modal
      state={modalState}
      onClose={onClose}
      title={collection == null ? 'Add a new collection' : `Update ${collection.name}`}
    >
      {collection == null ? (
        <CreateCollectionForm
          workspaceId={workspaceId}
          existingNames={existingNames}
          maxFileSizeBytes={maxFileSizeBytes}
          onDone={handleDone}
          onCancel={onClose}
        />
      ) : (
        <UpdateCollectionForm
          collection={collection}
          workspaceId={workspaceId}
          maxFileSizeBytes={maxFileSizeBytes}
          onDone={handleDone}
          onCancel={onClose}
        />
      )}
    </Modal>
  );
}

type CreateCollectionFormProps = {
  workspaceId: number;
  existingNames: string[];
  maxFileSizeBytes: number | undefined;
  onDone: () => void;
  onCancel: () => void;
};

function CreateCollectionForm({
  workspaceId,
  existingNames,
  maxFileSizeBytes,
  onDone,
  onCancel,
}: CreateCollectionFormProps) {
  const create = useCreateCollection({ workspaceId });
  const snackbars = useSnackbars();
  const [importSource, setImportSource] = useState<ImportSource | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const schema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .trim()
          .min(1, 'Collection name is required.')
          .refine((value) => !existingNames.includes(value), { message: 'Collection names must be unique' }),
        description: z.string(),
      }),
    [existingNames],
  );

  const form = useZodForm({ schema, defaultValues: { name: '', description: '' }, mode: 'onChange' });

  const handleImportSourceChange = (source: ImportSource | null) => {
    setImportSource(source);
    if (source != null) setImportError(null);
  };

  const handleSubmit = form.handleSubmit(async (values) => {
    // Uploading is checked first so an Enter-key submit — which bypasses the disabled
    // button — is a no-op mid-upload instead of claiming no file was imported.
    if (isUploading) return;
    if (importSource == null) {
      setImportError(IMPORT_REQUIRED_ERROR);
      return;
    }
    try {
      await create.mutateAsync(buildCreateCollectionBody(values.name.trim(), values.description, importSource));
      onDone();
    } catch {
      snackbars.create({ message: "Collection couldn't be created. Try again.", variant: 'critical' });
    }
  });

  return (
    <>
      <ModalBody>
        <Form id={CREATE_FORM_ID} onSubmit={handleSubmit}>
          {/* Fields sit directly in FormLayout (a Stack). FormLayoutGroup is a column grid
              (`grid-auto-flow: column`), so wrapping them would put the two text fields and
              the dropzone side by side in three columns. */}
          <FormLayout>
            <TextField
              {...form.registerPluma('name')}
              label="Name"
              placeholder="Name your collection"
              autofocus={true}
            />
            <TextArea
              {...form.registerPluma('description')}
              label="Description"
              placeholder="Describe your collection"
            />
            <ImportSourceField
              value={importSource}
              onChange={handleImportSourceChange}
              workspaceId={workspaceId}
              maxFileSizeBytes={maxFileSizeBytes}
              onUploadingChange={setIsUploading}
            />
            {importError == null ? null : <Banner variant="critical">{importError}</Banner>}
          </FormLayout>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          form={CREATE_FORM_ID}
          variant="primary"
          isLoading={create.isPending}
          isDisabled={!form.formState.isValid || isUploading}
        >
          Add collection
        </Button>
      </ModalFooter>
    </>
  );
}

type UpdateCollectionFormProps = {
  collection: Collection;
  workspaceId: number;
  maxFileSizeBytes: number | undefined;
  onDone: () => void;
  onCancel: () => void;
};

// Sheet only: the collection record carries no filename, so an s3 import can't be shown (AUD-4335).
function initialImportSource(collection: Collection): ImportSource | null {
  return collection.sheet == null ? null : { kind: 'sheet', sheet: collection.sheet };
}

function UpdateCollectionForm({
  collection,
  workspaceId,
  maxFileSizeBytes,
  onDone,
  onCancel,
}: UpdateCollectionFormProps) {
  const update = useUpdateCollection({ workspaceId });
  const snackbars = useSnackbars();
  const [importSource, setImportSource] = useState<ImportSource | null>(() => initialImportSource(collection));
  const [importError, setImportError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleImportSourceChange = (source: ImportSource | null) => {
    setImportSource(source);
    if (source != null) setImportError(null);
  };

  const handleSubmit = async () => {
    if (isUploading) return;
    if (importSource == null) {
      setImportError(IMPORT_REQUIRED_ERROR);
      return;
    }
    try {
      await update.mutateAsync({ id: collection.id, update: buildUpdateCollectionRequest(importSource) });
      onDone();
    } catch {
      snackbars.create({ message: "Collection couldn't be updated. Try again.", variant: 'critical' });
    }
  };

  return (
    <>
      <ModalBody>
        <FormLayout>
          <Banner variant="caution">
            Updating the collection will overwrite existing data. This cannot be undone.
          </Banner>
          <ImportSourceField
            value={importSource}
            onChange={handleImportSourceChange}
            workspaceId={workspaceId}
            maxFileSizeBytes={maxFileSizeBytes}
            onUploadingChange={setIsUploading}
          />
          {importError == null ? null : <Banner variant="critical">{importError}</Banner>}
        </FormLayout>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => void handleSubmit()}
          isLoading={update.isPending}
          isDisabled={update.isPending || isUploading}
        >
          Update collection
        </Button>
      </ModalFooter>
    </>
  );
}

export function useCollectionFormModal() {
  return useModal(CollectionFormModal);
}
